import api from './api';
import { ENDPOINTS, EMPLOYEE_PRODUCTS_PAGE_SIZE } from '../utils/constants';
import { withApiError } from '../utils/errorHandler';

/**
 * Product service functions for retrieving product catalog
 */

/**
 * Get all products with their variants
 * @returns {Promise<Array>} - Array of products with nested variants
 * @throws {Object} - Formatted error object
 */
export const getProducts = () =>
  withApiError('Get products', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.LIST);
    return response.data;
  });

/**
 * Get a single product by ID
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} - Product object with variants
 * @throws {Object} - Formatted error object
 */
export const getProduct = async (productId) => {
  if (!productId || typeof productId !== 'number') {
    throw { message: 'Invalid product ID' };
  }

  return withApiError('Get product', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.DETAIL(productId));
    return response.data;
  });
};

/**
 * Get all product variants
 * @returns {Promise<Array>} - Array of product variants
 * @throws {Object} - Formatted error object
 */
export const getProductVariants = () =>
  withApiError('Get product variants', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.VARIANTS);
    return response.data;
  });

/**
 * Get a single product variant by ID
 * @param {number} variantId - Product variant ID
 * @returns {Promise<Object>} - Product variant object
 * @throws {Object} - Formatted error object
 */
export const getProductVariant = async (variantId) => {
  if (!variantId || typeof variantId !== 'number') {
    throw { message: 'Invalid variant ID' };
  }

  return withApiError('Get product variant', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.VARIANT_DETAIL(variantId));
    return response.data;
  });
};


// ==================== Public Product Functions ====================

/**
 * Get the public wholesale product catalog (no auth required).
 * Returns only active products with name, description, grain varieties, and image URL.
 * No pricing or variant data included.
 * @returns {Promise<Array>} - Array of public product objects
 * @throws {Object} - Formatted error object
 */
export const getPublicProducts = () =>
  withApiError('Get public products', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.PUBLIC.LIST);
    return response.data;
  });


// ==================== Employee Product Functions ====================

export const getEmployeeProducts = (params = {}) =>
  withApiError('Get employee products', async () => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.is_active !== undefined && params.is_active !== '')
      searchParams.append('is_active', params.is_active);
    if (params.page) searchParams.append('page', params.page);
    if (params.ordering) searchParams.append('ordering', params.ordering);
    // Request all products in one page so client-side sorting works across the full set
    searchParams.append('page_size', String(EMPLOYEE_PRODUCTS_PAGE_SIZE));

    const qs = searchParams.toString();
    const url = qs
      ? `${ENDPOINTS.PRODUCTS.EMPLOYEE.LIST}?${qs}`
      : ENDPOINTS.PRODUCTS.EMPLOYEE.LIST;

    const response = await api.get(url);
    return response.data;
  });

export const getEmployeeProduct = (id) =>
  withApiError('Get employee product', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.EMPLOYEE.DETAIL(id));
    return response.data;
  });

export const createProduct = (data) =>
  withApiError('Create product', async () => {
    const response = await api.post(ENDPOINTS.PRODUCTS.EMPLOYEE.LIST, data);
    return response.data;
  });

export const updateProduct = (id, data) =>
  withApiError('Update product', async () => {
    const response = await api.put(ENDPOINTS.PRODUCTS.EMPLOYEE.DETAIL(id), data);
    return response.data;
  });

export const patchProduct = (id, data) =>
  withApiError('Patch product', async () => {
    const response = await api.patch(ENDPOINTS.PRODUCTS.EMPLOYEE.DETAIL(id), data);
    return response.data;
  });

export const deleteProduct = (id) =>
  withApiError('Delete product', async () => {
    await api.delete(ENDPOINTS.PRODUCTS.EMPLOYEE.DETAIL(id));
  });

export const reactivateProduct = (id) =>
  withApiError('Reactivate product', async () => {
    const response = await api.post(ENDPOINTS.PRODUCTS.EMPLOYEE.REACTIVATE(id));
    return response.data;
  });

export const setInventoryStatus = (id, inventoryStatus) =>
  withApiError('Set inventory status', async () => {
    const response = await api.patch(ENDPOINTS.PRODUCTS.EMPLOYEE.SET_INVENTORY_STATUS(id), {
      inventory_status: inventoryStatus,
    });
    return response.data;
  });

export const getProductChoices = () =>
  withApiError('Get product choices', async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.EMPLOYEE.CHOICES);
    return response.data;
  });

export const bulkSetCategory = (productIds, categoryIds) =>
  withApiError('Bulk set category', async () => {
    const response = await api.post(ENDPOINTS.PRODUCTS.EMPLOYEE.BULK_SET_CATEGORY, {
      product_ids: productIds,
      category_ids: categoryIds,
    });
    return response.data;
  });

/**
 * Upload (or replace) the label image for a product variant.
 * @param {number} variantId - ProductVariant ID
 * @param {File}   file      - Image file to upload
 * @returns {Promise<Object>} Updated variant serializer data (includes label_url)
 */
export const uploadVariantLabel = (variantId, file) =>
  withApiError('Upload variant label', async () => {
    const formData = new FormData();
    formData.append('label', file);
    const response = await api.post(
      ENDPOINTS.PRODUCTS.EMPLOYEE.VARIANT_UPLOAD_LABEL(variantId),
      formData,
      { headers: { 'Content-Type': undefined } },
    );
    return response.data;
  });

/**
 * Delete the label image for a product variant.
 * @param {number} variantId - ProductVariant ID
 * @returns {Promise<Object>} Updated variant serializer data
 */
export const deleteVariantLabel = (variantId) =>
  withApiError('Delete variant label', async () => {
    const response = await api.post(
      ENDPOINTS.PRODUCTS.EMPLOYEE.VARIANT_DELETE_LABEL(variantId),
    );
    return response.data;
  });

/**
 * Fetch the label image blob for a variant with auth.
 * Returns an object URL that can be used as an <img src> or window.open target.
 * Caller is responsible for calling URL.revokeObjectURL() when done.
 * @param {number} variantId - ProductVariant ID
 * @returns {Promise<string>} Object URL pointing to the image blob
 */
export const fetchVariantLabelBlobUrl = (variantId) =>
  withApiError('Fetch variant label', async () => {
    const response = await api.get(
      ENDPOINTS.PRODUCTS.EMPLOYEE.VARIANT_LABEL(variantId),
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  });


// ==================== Product Image Functions ====================

/**
 * Upload (or replace) the image for a product.
 * @param {number} productId - Product ID
 * @param {File}   file      - Image file to upload
 * @returns {Promise<Object>} Updated product serializer data (includes image_url)
 */
export const uploadProductImage = (productId, file) =>
  withApiError('Upload product image', async () => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(
      ENDPOINTS.PRODUCTS.EMPLOYEE.PRODUCT_UPLOAD_IMAGE(productId),
      formData,
      { headers: { 'Content-Type': undefined } },
    );
    return response.data;
  });

/**
 * Delete the image for a product.
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} Updated product serializer data
 */
export const deleteProductImage = (productId) =>
  withApiError('Delete product image', async () => {
    const response = await api.post(
      ENDPOINTS.PRODUCTS.EMPLOYEE.PRODUCT_DELETE_IMAGE(productId),
    );
    return response.data;
  });

/**
 * Fetch the product image blob with auth.
 * Returns an object URL that can be used as an <img src>.
 * Caller is responsible for calling URL.revokeObjectURL() when done.
 * @param {number} productId - Product ID
 * @returns {Promise<string>} Object URL pointing to the image blob
 */
export const fetchProductImageBlobUrl = (productId) =>
  withApiError('Fetch product image', async () => {
    const response = await api.get(
      ENDPOINTS.PRODUCTS.EMPLOYEE.PRODUCT_IMAGE(productId),
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  });
